/**
 * Created: 2026-10-08
 * Initial commit: Show coaching appointment dialog from admin slot grid (ef978969e)
 *
 * Read-only dialog showing a coaching appointment (course, coach, booked
 * court-hours, and students) when an admin clicks a coaching slot.
 */

import { gql } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import CloseIcon from '@mui/icons-material/Close'
import RefreshIcon from '@mui/icons-material/Refresh'
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { SPORT_LABEL } from '@tc-app/shared/labels'
import {
  AdminCoachingAppointmentDocument,
  CoachingAttendeeStatus,
  CoachingSessionStatus,
  RosterMode,
} from '../gql/graphql'
import { getWeekdayText } from '../lib/dateUtils'
import UserAvatar from './UserAvatar'

gql`
  query AdminCoachingAppointment($sessionId: UUID!) {
    coachingAppointment(sessionId: $sessionId) {
      __typename
      ... on CoachingAppointment {
        sessionId
        rosterMode
        status
        course {
          id
          name
          sport
          seats
        }
        coach {
          id
          openid
          name
          avatar
          mobile
          gender
        }
        slots {
          date
          hour
          court {
            id
            name
          }
        }
        attendees {
          status
          student {
            id
            openid
            name
            avatar
            mobile
            gender
          }
        }
      }
      ... on GraphqlRequestError {
        code
        message
      }
    }
  }
`

const APPOINTMENT_STATUS_CHIP: Record<
  CoachingSessionStatus,
  { label: string; color: 'success' | 'warning' | 'default' }
> = {
  [CoachingSessionStatus.Scheduled]: { label: '待确认', color: 'warning' },
  [CoachingSessionStatus.Confirmed]: { label: '已确认', color: 'success' },
  [CoachingSessionStatus.Cancelled]: { label: '已取消', color: 'default' },
}

const ATTENDEE_STATUS_CHIP: Record<
  CoachingAttendeeStatus,
  { label: string; color: 'success' | 'warning' | 'default' }
> = {
  [CoachingAttendeeStatus.Scheduled]: { label: '待确认', color: 'warning' },
  [CoachingAttendeeStatus.Confirmed]: { label: '已确认', color: 'success' },
  [CoachingAttendeeStatus.Cancelled]: { label: '已取消', color: 'default' },
}

const ROSTER_MODE_LABEL: Record<RosterMode, string> = {
  [RosterMode.Invite]: '邀请指定学员',
  [RosterMode.Enroll]: '公开报名',
}

const formatHour = (hour: number) => `${String(hour).padStart(2, '0')}:00`

interface AppointmentDialogProps {
  sessionId: string | null
  onClose: () => void
}

export default function AppointmentDialog({
  sessionId,
  onClose,
}: AppointmentDialogProps) {
  const { data, loading, error, refetch } = useQuery(AdminCoachingAppointmentDocument, {
    variables: { sessionId: sessionId ?? '' },
    skip: !sessionId,
    fetchPolicy: 'network-only',
  })

  const result = data?.coachingAppointment
  const appointment = result?.__typename === 'CoachingAppointment' ? result : undefined
  const requestError = result?.__typename === 'GraphqlRequestError' ? result : undefined

  // Group court-hours by date: "MM-dd 周X  HH:00-HH:00  court names"
  const slotRows = appointment
    ? [...new Set(appointment.slots.map((s) => s.date))].map((date) => {
        const daySlots = appointment.slots.filter((s) => s.date === date)
        const hours = daySlots.map((s) => s.hour)
        return {
          date,
          time: `${formatHour(Math.min(...hours))}-${formatHour(Math.max(...hours) + 1)}`,
          courts: [...new Set(daySlots.map((s) => s.court.name))].join('、'),
        }
      })
    : []

  const activeCount = appointment
    ? appointment.attendees.filter((a) => a.status !== CoachingAttendeeStatus.Cancelled)
        .length
    : 0

  return (
    <Dialog open={!!sessionId} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'relative',
        }}
      >
        课程预约
        <Box sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
          {appointment && (
            <Chip
              label={APPOINTMENT_STATUS_CHIP[appointment.status].label}
              color={APPOINTMENT_STATUS_CHIP[appointment.status].color}
              size="small"
            />
          )}
        </Box>
        <Box sx={{ ml: 'auto', display: 'flex' }}>
          <IconButton onClick={() => refetch()} size="small" disabled={loading}>
            <RefreshIcon />
          </IconButton>
          <IconButton onClick={onClose} size="small" aria-label="关闭">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: 'grey.100', p: 1.5 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && <Alert severity="error">{error.message}</Alert>}
        {requestError && (
          <Alert severity="error">{requestError.message ?? requestError.code}</Alert>
        )}

        {appointment && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                  课程信息
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {appointment.course.name}
                  </Typography>
                  <Chip label={SPORT_LABEL[appointment.course.sport]} size="small" />
                  <Chip
                    label={ROSTER_MODE_LABEL[appointment.rosterMode]}
                    size="small"
                    variant="outlined"
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    教练
                  </Typography>
                  <UserAvatar user={appointment.coach} />
                </Box>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                  上课时间
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>日期</TableCell>
                      <TableCell>时间</TableCell>
                      <TableCell>场地</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {slotRows.map((row) => (
                      <TableRow key={row.date}>
                        <TableCell>
                          {row.date} {getWeekdayText(row.date)}
                        </TableCell>
                        <TableCell>{row.time}</TableCell>
                        <TableCell>{row.courts}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                  学员 ({activeCount}/{appointment.course.seats})
                </Typography>
                {appointment.attendees.length === 0 ? (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    暂无学员
                  </Typography>
                ) : (
                  <Table size="small">
                    <TableBody>
                      {appointment.attendees.map((a) => (
                        <TableRow key={a.student.openid}>
                          <TableCell>
                            <UserAvatar user={a.student} />
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={ATTENDEE_STATUS_CHIP[a.status].label}
                              color={ATTENDEE_STATUS_CHIP[a.status].color}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}
